const resolveApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:8000/api';
  }
  return 'https://carsinfinite.com/api';
};

const API_URL = resolveApiUrl();

const getAccessToken = () =>
  localStorage.getItem('accessToken') || localStorage.getItem('cftoken');

export { getAccessToken };

export const setAccessToken = (token) => {
  if (token) {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('cftoken', token);
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('cftoken');
  }
};

let refreshPromise = null;

const refreshAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Session expired');
        setAccessToken(data.accessToken);
        return data.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export const apiRequest = async (path, options = {}, retry = true) => {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
    body:
      options.body && typeof options.body !== 'string'
        ? JSON.stringify(options.body)
        : options.body,
  });

  let data = {};
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    data = await res.json().catch(() => ({}));
  } else {
    const text = await res.text().catch(() => '');
    if (res.status === 404) {
      throw new Error('This action is not available on the server. Restart the backend and try again.');
    }
    throw new Error(text?.slice(0, 120) || `Request failed (${res.status})`);
  }

  if (res.status === 401 && retry && !path.includes('/auth/')) {
    try {
      await refreshAccessToken();
      return apiRequest(path, options, false);
    } catch {
      setAccessToken(null);
      throw new Error(data.message || 'Unauthorized');
    }
  }

  if (!res.ok) {
    throw new Error(data.message || 'Request failed');
  }

  return data;
};

export const authApi = {
  signup: (body) => apiRequest('/auth/signup', { method: 'POST', body }),
  login: (body) => apiRequest('/auth/login', { method: 'POST', body }),
  logout: () => apiRequest('/auth/logout', { method: 'POST' }),
  me: () => apiRequest('/auth/me'),
  refresh: () => apiRequest('/auth/refresh', { method: 'POST' }),
  updateProfile: (body) => apiRequest('/auth/profile', { method: 'PUT', body }),
  submitKyc: (body) => apiRequest('/auth/kyc', { method: 'PUT', body }),
  changePassword: (body) => apiRequest('/auth/password', { method: 'PUT', body }),
};

export const listingApi = {
  submit: (body) => apiRequest('/cars/submit', { method: 'POST', body }),
  mine: () => apiRequest('/cars/mine'),
  get: (id) => apiRequest(`/cars/mine/${id}`),
  update: (id, body) => apiRequest(`/cars/mine/${id}`, { method: 'PUT', body }),
  remove: (id) => apiRequest(`/cars/mine/${id}`, { method: 'DELETE' }),
};

export const contactApi = {
  submit: (body) => apiRequest('/contact/inquiries', { method: 'POST', body }),
};

export const valuationApi = {
  estimate: (body) => apiRequest('/valuation/estimate', { method: 'POST', body }),
};

export const testDriveApi = {
  book: (body) => apiRequest('/test-drives', { method: 'POST', body }),
  mine: () => apiRequest('/test-drives/mine'),
};

export const protectionOrderApi = {
  create: (body) => apiRequest('/protection-orders', { method: 'POST', body }),
  mine: () => apiRequest('/protection-orders/mine'),
  requestReturn: (id) => apiRequest(`/protection-orders/${id}/return`, { method: 'POST' }),
};

export const wishlistApi = {
  list: () => apiRequest('/wishlist'),
  add: (carId) => apiRequest(`/wishlist/${carId}`, { method: 'POST' }),
  remove: (carId) => apiRequest(`/wishlist/${carId}`, { method: 'DELETE' }),
  sync: (ids) => apiRequest('/wishlist/sync', { method: 'POST', body: { ids } }),
};

export const fetchBootstrap = () => apiRequest('/public/bootstrap');

export const crudApi = (resource) => ({
  list: () => apiRequest(`/${resource}`),
  get: (id) => apiRequest(`/${resource}/${id}`),
  create: (body) => apiRequest(`/${resource}`, { method: 'POST', body }),
  update: (id, body) => apiRequest(`/${resource}/${id}`, { method: 'PUT', body }),
  remove: (id) => apiRequest(`/${resource}/${id}`, { method: 'DELETE' }),
});

export const siteConfigApi = {
  get: (key) => apiRequest(`/site-config/${key}`),
  update: (key, data) =>
    apiRequest(`/site-config/${key}`, { method: 'PUT', body: { data } }),
  list: () => apiRequest('/site-config'),
};

export const adminApi = {
  analytics: () => apiRequest('/admin/analytics'),
  activeUsers: () => apiRequest('/admin/active-users'),
  notifications: {
    list: () => apiRequest('/admin/notifications'),
    create: (body) => apiRequest('/admin/notifications', { method: 'POST', body }),
    remove: (id) => apiRequest(`/admin/notifications/${id}`, { method: 'DELETE' }),
  },
  partners: {
    list: () => apiRequest('/admin/partners'),
    create: (body) => apiRequest('/admin/partners', { method: 'POST', body }),
    update: (id, body) => apiRequest(`/admin/partners/${id}`, { method: 'PUT', body }),
    remove: (id) => apiRequest(`/admin/partners/${id}`, { method: 'DELETE' }),
    cars: (id) => apiRequest(`/admin/partners/${id}/cars`),
    assignCarSubRegister: (carId, sub_register) =>
      apiRequest(`/admin/cars/${carId}/sub-register`, { method: 'PUT', body: { sub_register } }),
  },
};

export const partnerApi = {
  me: () => apiRequest('/partner/me'),
  stats: () => apiRequest('/partner/stats'),
  cars: (status) =>
    apiRequest(status ? `/partner/cars?status=${encodeURIComponent(status)}` : '/partner/cars'),
  updateCar: (id, body) => apiRequest(`/partner/cars/${id}`, { method: 'PUT', body }),
  testDrives: () => apiRequest('/partner/test-drives'),
  updateTestDrive: (id, body) =>
    apiRequest(`/partner/test-drives/${id}`, { method: 'PUT', body }),
};

export const notificationApi = {
  list: () => apiRequest('/auth/notifications'),
  markRead: (id) => apiRequest(`/auth/notifications/${id}/read`, { method: 'PATCH' }),
  markAllRead: () => apiRequest('/auth/notifications/read-all', { method: 'PATCH' }),
};

export const presenceApi = {
  ping: () => apiRequest('/auth/presence', { method: 'POST' }),
};

export default API_URL;
